<template>
  <aw-product-list
    :is-whole-loading-completed="isWholeLoadingCompleted"
    :is-more-loading-completed="isMoreLoadingCompleted"
    :item-count="itemCount"
    @load-more-product-items="loadMoreProductItems"
    @order-product-items="orderProductItems"
    @filter-product-items="throttleFilter"
  >
    <template #content>
      <aw-product-list-header v-if="itemCount" :product-counter="{ all: itemCount, loaded: products.length }" :breadcrumbs="breadcrumbs">
        <template #listName>
          <lv-heading
            :ref="Navigation[ID_PRODUCT_LIST_TOP]"
            tag="h1"
            level="2"
            version2
            :class="$style.pageTitle"
          >
            {{ categoryName }}
          </lv-heading>
        </template>
        <template #filters>
          <div
            :class="$style.filterWrapper"
          >
            <client-only>
              <template v-if="screenRange['mobile-max']">
                <aw-category-selection-filter
                  v-if="hasCategorySelectionFilter"
                  :is-list-loading="!isMoreLoadingCompleted"
                  view="COMPACT"
                  show-title
                  @activate-filter="throttleFilter"
                />
                <aw-subcategory-filter
                  v-if="isProductListPage"
                  view="COMPACT"
                  @activate-filter="throttleFilter"
                />
              </template>
              <aw-product-list-order-by-and-mobile-filter-modal
                v-else
                is-sortable
                :is-filterable="false"
                controls-visible-status
              />
            </client-only>
          </div>
        </template>
      </aw-product-list-header>
    </template>
  </aw-product-list>
</template>

<script>
  import { mapState } from 'pinia';
  import { defineAsyncComponent } from 'vue';
  import { LvHeading } from '~~/common/components/loginet-vue-shop/index';
  import { Navigation, ID_PRODUCT_LIST_TOP, focusRef } from '~~/common/config/Navigation';
  import AwProductList from '~~/common/components/Page/ProductList/AwProductList';
  import AwProductListHeader from '~~/common/components/Page/ProductList/AwProductListHeader.vue';

  import { useUrlResourceStore } from '~~/common/stores/urlResource';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useProductsStore } from '~~/common/stores/products';
  import urlMixin from '~~/common/mixins/urlMixin.js';
  import pageTypeMixin from '~~/common/mixins/pageTypeMixin.js';
  import { useCategorySelectionFilter } from '~~/common/composables/useCategorySelectionFilter';
  import { getCategoryDisplayName } from '~~/common/utils/category';

  export default {
    name: 'AwProductListPage',
    components: {
      AwProductList,
      AwProductListHeader,
      AwSubcategoryFilter: defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwSubcategoryFilter.vue')),
      AwCategorySelectionFilter: defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwCategorySelectionFilter.vue')),
      LvHeading,
      AwProductListOrderByAndMobileFilterModal: defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwProductListOrderByAndMobileFilterModal')),
    },
    mixins: [urlMixin, pageTypeMixin],
    props: {
      category: {
        type: Object,
        required: true,
      },
    },
    setup () {
      return useCategorySelectionFilter();
    },
    data () {
      return {
        Navigation,
        ID_PRODUCT_LIST_TOP,
        isWholeLoadingCompleted: true,
        isMoreLoadingCompleted: true,
        timeoutId: null,
      };
    },
    computed: {
      categoryId () {
        return this.category?.id;
      },
      categoryName () {
        return getCategoryDisplayName(this.category);
      },
      ...mapState(useUrlResourceStore, {
        // get boutiqueFalId from resourceId:"1|7940"
        boutiqueId: state => state.data.resourceId.split('|')[1],
      }),
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      ...mapState(useProductsStore, {
        currentPage: state => state.currentPage,
        itemsPerPage: state => state.itemsPerPage,
        itemCount: state => state.itemCount,
        filterBy: state => state.filterBy,
        sortKey: state => state.sortKey,
        sortBy: state => state.sortBy,
        refreshProducts: state => state.refreshProducts,
        products: state => state.renderedProducts,
        productsCategories: state => state.productsCategories,
        flagFilter: state => state.flagFilter,
      }),
      breadcrumbs () {
        const calculateCategoresBreacrumb = (categories) => {
          const copyCategories = [...categories];
          const parentSlug = [];
          const breadcrumbs = [];
          for (const category of copyCategories.reverse()) {
            parentSlug.push(category.slug);
            breadcrumbs.push({
              token: getCategoryDisplayName(category),
              to: this.getCategoryUrl({
                ...category,
                parentSlug: parentSlug.join('/'),
              }),
              categoryId: category.id,
            });
          }
          return breadcrumbs;
        };
        return [
          {
            to: this.$localePath('/'),
            disabled: false,
            token: this.$awt('aw.common.category.home'),
          },
          ...(__AW_IS_SHOP__ ? [{
            to: this.$localePath('/shop'),
            disabled: false,
            token: this.$awt('aw.common.category.shop'),
          }] : []),
          ...((this.productsCategories?.length)
            ? calculateCategoresBreacrumb(this.productsCategories)
            : [{
              token: this.categoryName,
              to: '/',
              disabled: true,
            }]
          ),
        ];
      },
    },
    watch: {
      refreshProducts () {
        this.throttleFilter();
      },
    },
    mounted () {
      focusRef(this, { refId: Navigation[ID_PRODUCT_LIST_TOP] });
    },
    beforeUnmount () {
      clearTimeout(this.timeoutId);
    },
    methods: {
      async loadMoreProductItems (loadingState) {
        const urlResourceStore = useUrlResourceStore();
        const productsStore = useProductsStore();
        this.isMoreLoadingCompleted = false;

        if (loadingState && this.itemCount === this.products.length) {
          loadingState.complete();
          return;
        }

        let payload = {
          isCached: true,
          page: this.currentPage + 1,
          itemsPerPage: this.itemsPerPage,
          categoryId: this.categoryId,
          filterParams: this.filterBy,
          sortKey: this.sortKey || null,
          sortBy: this.sortBy || null,
        };
        if (urlResourceStore.data.resourceType === 'boutique') {
          delete payload.categoryId;
          payload = {
            ...payload,
            boutiqueId: this.boutiqueId,
          };
        }

        await productsStore.fetchProducts(payload).then((result) => {
          if (loadingState) {
            if (!result || this.itemCount === this.products.length) {
              loadingState.complete();
            } else {
              loadingState.loaded();
            }
          }
          this.isMoreLoadingCompleted = true;
        });
      },
      async orderProductItems ({
        value: {
          sortBy,
          sortKey,
        },
      }) {
        const urlResourceStore = useUrlResourceStore();
        const productsStore = useProductsStore();
        let payload = {
          isCached: true,
          itemsPerPage: this.currentPage * this.itemsPerPage,
          page: 1,
          filterParams: this.filterBy,
          categoryId: this.categoryId,
          sortBy,
          sortKey,
        };
        if (urlResourceStore.data.resourceType === 'boutique') {
          delete payload.categoryId;
          payload = {
            ...payload,
            boutiqueId: this.boutiqueId,
          };
        }

        this.isMoreLoadingCompleted = false;
        await productsStore.fetchProducts(payload).finally(() => {
          this.isMoreLoadingCompleted = true;
        });
      },
      throttleFilter () {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => this.filterProductItems(), 800);
      },
      async filterProductItems () {
        const urlResourceStore = useUrlResourceStore();
        const productsStore = useProductsStore();
        this.isMoreLoadingCompleted = false;
        let payload = {
          isCached: true,
          page: 1,
          itemsPerPage: this.currentPage * this.itemsPerPage,
          categoryId: this.categoryId,
          filterParams: this.filterBy,
          sortKey: this.sortKey || null,
          sortBy: this.sortBy || null,
          flagId: this.flagFilter || null,
        };

        if (urlResourceStore.data.resourceType === 'boutique') {
          delete payload.categoryId;
          payload = {
            ...payload,
            boutiqueId: this.boutiqueId,
          };
        }
        await productsStore.fetchProducts(payload).finally(() => {
          this.isMoreLoadingCompleted = true;
        });
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
.pageTitle {
  font-size: 22px;
  line-height: 28px;

  @include tablet(min) {
    font-size: 26px;
    line-height: 32px;
  }
}

.promotion {
  @include lg() {
    margin-bottom: 27px;
  }
  margin: 23px 0 18px;
}

.filterWrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;

  @include mobile(max) {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}
</style>
